<template>
  <v-container class="text-body-3-1">
    <v-row class="hint"> Should we send emails when the Prior Auth status changes? </v-row>

    <v-row v-if="loading">
      <v-col :cols="12">Loading…</v-col>
    </v-row>
    <v-row v-else class="selectable-row">
      <v-col :cols="1" class="py-0">
        <v-switch
          :model-value="emailsEnabledSelected"
          density="compact"
          hide-details
          color="primary"
          class=""
          @update:model-value="toggleEmailsEnabled($event)" />
      </v-col>
      <v-col :cols="10" class="">
        <span class="">Send emails</span>
      </v-col>
    </v-row>

    <v-row class="hint">
      Pick the statuses that will trigger a status notification email on the Prior Auth table. Changes apply to everyone
      on this company. Notifications will go to the users who submitted the Prior Auth.
    </v-row>

    <v-row v-if="loading">
      <v-col :cols="12">Loading…</v-col>
    </v-row>
    <template v-else>
      <v-row v-for="opt in options" :key="opt.value" class="selectable-row">
        <v-col :cols="1" class="">
          <v-switch
            :model-value="notfStatusesSelected.has(opt.value)"
            density="compact"
            hide-details
            color="primary"
            class=""
            @update:model-value="toggle(opt.value, $event)" />
        </v-col>
        <v-col :cols="10" class="">
          <span class="">{{ opt.label }}</span>
        </v-col>
      </v-row>
    </template>

    <v-divider class="my-4" />

    <v-row>
      <v-col :cols="6">
        <v-btn variant="text" size="small" :loading="saving" :disabled="!hasChanges" @click="reset">Reset</v-btn>
      </v-col>
      <v-col :cols="6" class="text-end">
        <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
          Save
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="12">
        <transition name="status_notf-fade">
          <span v-if="error" class="text-error">{{ error }}</span>
          <span v-else-if="successMsg">{{ successMsg }}</span>
        </transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { settingsService } from "@/services/settingsService";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";
import {
  NEEDS_ATTENTION_DISPLAY,
  NEEDS_ATTENTION_STATUS_KEY,
  PA_STATUS_LABEL_BY_KEY,
  PA_STATUS_VALUE_BY_KEY,
} from "@/models/paStatus";

const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();

const loading = ref(false);
const notfStatusesInitial = ref(new Set());
const notfStatusesSelected = ref(new Set());
const emailsEnabledInitial = ref(false);
const emailsEnabledSelected = ref(false);

const statusOption = (key) => ({ value: PA_STATUS_VALUE_BY_KEY[key], label: PA_STATUS_LABEL_BY_KEY[key] });

const options = [
  statusOption("CLINICAL_REVIEW"),
  statusOption("REQUIRES_MORE_INFORMATION"),
  { value: NEEDS_ATTENTION_STATUS_KEY, label: `${NEEDS_ATTENTION_DISPLAY} (automation errors)` },
  statusOption("APPROVED"),
  statusOption("DENIED"),
  statusOption("PA_NOT_REQUIRED"),
];

const hasChanges = computed(() => {
  // Proxy object cannot use the Set.difference() method, so we create a new Set.
  return (
    new Set(notfStatusesSelected.value).symmetricDifference(notfStatusesInitial.value).size > 0 ||
    emailsEnabledSelected.value !== emailsEnabledInitial.value
  );
});

onMounted(async () => {
  _load();
});

async function _load() {
  loading.value = true;
  error.value = "";
  try {
    const [emailsEnabled, notfStatuses] = await Promise.all([
      settingsService.getPriorAuthStatusChangeEmailsEnabled(),
      settingsService.getPriorAuthStatusChangeNotfStatuses(),
    ]);
    notfStatusesInitial.value = new Set(notfStatuses.statuses);
    notfStatusesSelected.value = new Set(notfStatuses.statuses);
    emailsEnabledInitial.value = emailsEnabled.enabled;
    emailsEnabledSelected.value = emailsEnabled.enabled;
    loading.value = false;
  } catch {
    // Keep loading state so the user cannot toggle values if the settings did not load
    error.value = "Failed to load status notification settings.";
  }
}

function toggle(statusKey, enabled) {
  if (enabled) notfStatusesSelected.value.add(statusKey);
  else notfStatusesSelected.value.delete(statusKey);
}

function toggleEmailsEnabled(enabled) {
  emailsEnabledSelected.value = enabled;
}

function reset() {
  notfStatusesSelected.value = new Set(notfStatusesInitial.value);
  emailsEnabledSelected.value = emailsEnabledInitial.value;
}

async function save() {
  await runSave(async () => {
    const [response1, response2] = await Promise.all([
      settingsService.updatePriorAuthStatusChangeNotfStatuses(Array.from(notfStatusesSelected.value)),
      settingsService.updatePriorAuthStatusChangeEmailsEnabled(emailsEnabledSelected.value),
    ]);
    notfStatusesInitial.value = new Set(response1.statuses);
    notfStatusesSelected.value = new Set(response1.statuses);
    emailsEnabledInitial.value = response2.enabled;
    emailsEnabledSelected.value = response2.enabled;
  });
}
</script>

<style lang="scss" scoped>
.v-row {
  align-items: center;
  margin: 2px 0px 4px 0px;

  &.selectable-row {
    cursor: pointer;
    transition: background-color $lh_transition_fast;

    &:hover {
      background: $lh_color_snowy_white;
    }
  }
}

.v-col {
  padding: 2px 0px 0px 0px;
}

.hint {
  color: $lh_color_blue_gray;
}
</style>
